import { ColumnDef } from "@tanstack/react-table";
import { dealTypesRadioButtons, getNumberFormatted } from "@/lib/app";
import { capitalizeFirstLetter } from "@revelate/utils";
import { format, isBefore, isAfter, differenceInDays } from "date-fns";
import { UTCDate } from "@date-fns/utc";
import { DateRange, NestedDeal } from "@revelate/types";
import { DataTableColumnHeader } from "../../deals/components/data-table-column-header";
import { Badge } from "@/components/ui/badge";

export function getDealReviewColumns(): ColumnDef<NestedDeal>[] {
  return [
    // First the visible columns
    {
      accessorKey: "name",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Deal" />
      ),
      cell: ({ row }) => {
        const name = row.getValue("name")?.toString() || "";
        return (
          <p className="group-hover:underline max-w-[300px] truncate font-medium">
            {name}
          </p>
        );
      },
    },
    {
      accessorKey: "owner_name",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Owner" />
      ),
      filterFn: (row, id, value) => {
        const rowValue = (row.getValue(id) ?? "No owner") as string;
        return value.includes(rowValue);
      },
    },
    {
      accessorKey: "value",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Value" />
      ),
      cell: ({ row }) => {
        return (
          <div className="shrink-0 min-w-28">
            {new Intl.NumberFormat("sv-SE").format(row.original.value)} {row.original.currency_code}
          </div>
        );
      },
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
    },
    {
      accessorKey: "stage_probability",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Stage" />
      ),
      cell: ({ row }) => (
        <div className="flex flex-col">
          <p>
            {/* @ts-ignore */}
            {row.original.stage_label}
          </p>
          <p className="text-xs text-gray-500">
            {/* @ts-ignore */}
            {row.original.pipeline_label ?? ""}
          </p>
        </div>
      ),
      filterFn: (row, id, value) => {
        const rowValue = (row.getValue(id) ?? "No owner") as string;
        return value.includes(rowValue);
      },
    },
    {
      accessorKey: "closed_at",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Close" />
      ),
      cell: ({ row }) => {
        const closeDate = new UTCDate(row.getValue("closed_at"));
        const year = closeDate.getFullYear();
        if (year < 2020) { return (<div>Not set</div>) }

        const daysToClose = differenceInDays(closeDate, new Date());

        return (
          <div className="flex flex-col items-start">
            <p>
              {format(closeDate, "yyyy-MM-dd")}
            </p>
            <p className="text-xs text-gray-500">
              {daysToClose === 0 ? (
                <span className="text-green-600">Today</span>
              ) : daysToClose < 0 ? (
                <span className="text-red-400">{-daysToClose}d ago</span>
              ) : (
                <span>in {daysToClose}d</span>
              )}
            </p>
          </div>
        );
      },
      filterFn: (row, _id, value: DateRange | undefined) => {
        if (!row.original.closed_at) return false;
        if (!value) return true;
        const afterFromDate = !value.from || isAfter(row.original.closed_at, value.from);
        const beforeToDate = !value.to || isBefore(row.original.closed_at, value.to);
        return afterFromDate && beforeToDate;
      },
    },
    {
      accessorKey: "weighted_risk_score",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Risk" />
      ),
      cell: ({ row }) => {
        //@ts-ignore
        const riskBucket = row.original.weighted_risk_score >= 50
          ? "high"
          //@ts-ignore
          : row.original.weighted_risk_score >= 25
            ? "medium"
            : "low";
        const badgeVariants = {
          high: "rejected",
          medium: "approved",
          low: "paid",
        };

        return (
          <Badge
            variant={badgeVariants[riskBucket] as any}
            className="capitalize"
          >
            {riskBucket}
          </Badge>
        );
      },
    },

    // Hidden columns only used for filtering
    // Should be possible to remove some or all of these
    // depending on what the using components expect to be present or not
    {
      accessorKey: "id",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Deal" />
      ),
      cell: ({ row }) => <div className="w-[80px]">{row.getValue("id")}</div>,
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "stage_label",
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
    },
    {
      accessorKey: "onboarding_value",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Onboarding Value" />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center">
            {getNumberFormatted(row.getValue("onboarding_value"))}
          </div>
        );
      },
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
    },
    {
      accessorKey: "currency_code",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Currency" />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center">
            {row.getValue("currency_code")}
          </div>
        );
      },
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
    },
    {
      accessorKey: "deal_type",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Deal type" />
      ),
      cell: ({ row }) => {
        const dealType = dealTypesRadioButtons.find(
          (d) => d.value === row.getValue("deal_type")
        );

        if (!dealType) {
          return null;
        }

        return (
          <div className="flex items-center">
            <span>{dealType.label} business</span>
          </div>
        );
      },
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
    },
    {
      accessorKey: "provider",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Provider" />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center">
            {capitalizeFirstLetter(row.getValue("provider"))}
          </div>
        );
      },
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
    },
    {
      accessorKey: "provider_id",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Provider ID" />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center">
            {row.getValue("provider_id")}
          </div>
        );
      },
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
    },
    {
      accessorKey: "pipeline_label",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Pipeline" />
      ),
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
    },
    {
      accessorKey: "is_read_only",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Read Only" />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center">
            {row.getValue("is_read_only") ? "YES" : "NO"}
          </div>
        );
      },
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
    },
    {
      accessorKey: "is_closed",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Closed" />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center">
            {row.getValue("is_closed") ? "YES" : "NO"}
          </div>
        );
      },
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
    },
    {
      accessorKey: "is_won",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Won" />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center">
            {row.getValue("is_won") ? "YES" : "NO"}
          </div>
        );
      },
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
    },
    {
      accessorKey: "is_invoiced",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Invoiced" />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center">
            {row.getValue("is_invoiced") ? "YES" : "NO"}
          </div>
        );
      },
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
    },
    {
      accessorKey: "is_paid",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Paid" />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center">
            {row.getValue("is_paid") ? "YES" : "NO"}
          </div>
        );
      },
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
    },
    {
      accessorKey: "days_to_close",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Days to close" />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center">
            {row.getValue("days_to_close")}
          </div>
        );
      },
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
    },
    {
      accessorKey: "created_at",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Created at" />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center">
            {format(
              new UTCDate(row.getValue("created_at")),
              "yyyy-MM-dd HH:mm:ss"
            )}
          </div>
        );
      },
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
    },
  ];
};

